import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonIcon,
  IonButton,
  IonSegment,
  IonSegmentButton,
  IonLabel
} from '@ionic/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Sector
} from 'recharts';
import {
  barChartOutline,
  peopleOutline,
  thumbsUpOutline,
  eyeOutline,
  downloadOutline,
  trendingUpOutline,
  calendarOutline,
} from 'ionicons/icons';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import Loader from '../components/Loader';

const PollAnalytics = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [poll, setPoll] = useState(null);
  const [analytics, setAnalytics] = useState({
    totalVotes: 0,
    uniqueVoters: 0,
    viewCount: 0,
    answers: []
  });
  const [activeSegment, setActiveSegment] = useState('overview');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchPollAnalytics();
  }, [id]);

  const getExportSafeSvg = (iconData, color) => {
    if (!iconData || typeof iconData !== 'string') return null;

    if (iconData.includes('utf8,')) {
      try {
        const svgContent = iconData.split('utf8,')[1];
        // Inject color and dimensions directly into the SVG code
        // We remove any existing width/height and add our own for consistent container scaling
        const styledSvg = svgContent
          .replace(/<svg /, `<svg width="100%" height="100%" fill="${color}" stroke="${color}" `)
          .replace(/currentColor/g, color);
        return styledSvg;
      } catch (e) {
        console.error('Error processing icon for export:', e);
        return null;
      }
    }
    return iconData;
  };

  const fetchPollAnalytics = async () => {

    try {
      setLoading(true);

      // Fetch poll details
      const response = await axios.get(`${API_BASE_URL}/get_poll_simple.php?id=${id}`);
      if (response.data.success) {
        const pollData = response.data.poll;

        // Security check for Organization Admins (role 2)
        const userRoleId = sessionStorage.getItem('role_id');
        const userId = sessionStorage.getItem('user_id');

        if (userRoleId === '2' && String(pollData.user_id) !== String(userId)) {
          console.error('Access denied to poll analytics');
          setPoll(null);
          setLoading(false);
          return;
        }

        setPoll(pollData);

        // Calculate analytics from poll data
        // Answers already come parsed from backend
        const answers = Array.isArray(pollData.answers) ? pollData.answers : [];

        const totalVotes = parseInt(pollData.totalvotes) || answers.reduce((sum, answer) => sum + parseInt(answer.votes || 0), 0);

        setAnalytics({
          totalVotes,
          uniqueVoters: Math.ceil(totalVotes / 2), // Assuming max 2 votes per user
          viewCount: totalVotes * 3, // Estimate views as 3x votes
          answers: answers.map(answer => ({
            ...answer,
            votes: parseInt(answer.votes) || 0,
            percentage: totalVotes > 0 ? ((parseInt(answer.votes) || 0) / totalVotes * 100).toFixed(1) : 0
          }))
        });
      }
    } catch (error) {
      console.error('Error fetching poll analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportAnalytics = async () => {
    setIsExporting(true);

    // --- HACK TO PREVENT HTML2CANVAS STENCIL CRASH ---
    let originalAdoptedSet;
    const descriptor = Object.getOwnPropertyDescriptor(ShadowRoot.prototype, 'adoptedStyleSheets');
    if (descriptor && descriptor.set) {
      originalAdoptedSet = descriptor.set;
      Object.defineProperty(ShadowRoot.prototype, 'adoptedStyleSheets', {
        set: function (sheets) {
          try {
            originalAdoptedSet.call(this, sheets);
          } catch (e) {
            // Suppress "Sharing constructed stylesheets" crash
          }
        },
        configurable: true
      });
    }

    // Wait for the DOM to update and render all tabs
    setTimeout(async () => {
      try {
        const element = document.getElementById('analytics-export-container');
        if (!element) return;

        // Ensure html2pdf is available by loading it dynamically from CDN
        if (typeof window.html2pdf === 'undefined') {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        const opt = {
          margin: 0.2, // smaller margin to fit charts better
          filename: `poll-${id}-analytics-report.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: true }, // Enabled logging for better debugging
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
        };

        await window.html2pdf().set(opt).from(element).save();
      } catch (err) {
        console.error('Failed to export PDF:', err);
      } finally {
        setIsExporting(false);
        // Restore original setter
        if (originalAdoptedSet) {
          Object.defineProperty(ShadowRoot.prototype, 'adoptedStyleSheets', {
            set: originalAdoptedSet,
            configurable: true
          });
        }
      }
    }, 1200); // Increased timeout to 1200ms to ensure SVGs and charts are fully ready
  };


  if (!poll) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar style={{ '--background': 'linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)' }}>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/admin/polls" style={{ color: '#fff' }} />
            </IonButtons>
            <IonTitle style={{ color: '#fff', fontWeight: 'bold' }}>Poll Not Found</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent style={{ '--background': 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 50%, #99f6e4 100%)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>😕</div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#0f766e', marginBottom: '0.5rem' }}>Poll Not Found</h2>
              <p style={{ color: '#5eead4' }}>This poll may have been deleted or doesn't exist.</p>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const statCards = [
    {
      icon: thumbsUpOutline,
      value: analytics.totalVotes,
      label: 'Total Votes',
      gradient: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
      iconBg: 'rgba(20, 184, 166, 0.15)',
      iconColor: '#0d9488'
    },
    {
      icon: peopleOutline,
      value: analytics.uniqueVoters,
      label: 'Unique Voters',
      gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
      iconBg: 'rgba(16, 185, 129, 0.15)',
      iconColor: '#059669'
    },
    {
      icon: eyeOutline,
      value: analytics.viewCount,
      label: 'Total Views',
      gradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
      iconBg: 'rgba(6, 182, 212, 0.15)',
      iconColor: '#0891b2'
    }
  ];

  const answerColors = [
    { gradient: 'linear-gradient(135deg, #0d9488, #14b8a6)', bg: 'rgba(20, 184, 166, 0.08)', text: '#0f766e' },
    { gradient: 'linear-gradient(135deg, #059669, #10b981)', bg: 'rgba(16, 185, 129, 0.08)', text: '#047857' },
    { gradient: 'linear-gradient(135deg, #0891b2, #06b6d4)', bg: 'rgba(6, 182, 212, 0.08)', text: '#0e7490' },
    { gradient: 'linear-gradient(135deg, #7c3aed, #8b5cf6)', bg: 'rgba(139, 92, 246, 0.08)', text: '#6d28d9' },
    { gradient: 'linear-gradient(135deg, #db2777, #ec4899)', bg: 'rgba(236, 72, 153, 0.08)', text: '#be185d' },
    { gradient: 'linear-gradient(135deg, #ea580c, #f97316)', bg: 'rgba(249, 115, 22, 0.08)', text: '#c2410c' }
  ];

  // Glassmorphism card style
  const glassCard = {
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '24px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 8px 32px rgba(13, 148, 136, 0.12)'
  };

  return (
    <IonPage className="view-page">
      <IonHeader className="ion-no-border" translucent={true}>
        <IonToolbar className="analytics-header frosted-header">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/admin/polls" style={{ '--color': '#1e293b' }} />
          </IonButtons>
          <IonTitle style={{ fontFamily: 'Montserrat', fontWeight: '700', color: '#1e293b' }}>
            <div className="flex items-center gap-2 uppercase tracking-tight">
              <span>Polling Intelligence</span>
            </div>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={exportAnalytics} className="export-btn-premium">
              <IonIcon icon={downloadOutline} slot="start" />
              EXPORT REPORT
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="view-content">
        {loading && <Loader />}
        <div className="polls-container p-6 space-y-8">

          {/* Analysis View Selector */}
          <div className="flex justify-center mb-4">
            <IonSegment
              value={activeSegment}
              onIonChange={e => setActiveSegment(e.detail.value)}
              className="premium-segment-control"
              style={{ maxWidth: '600px', '--background': 'rgba(255,255,255,0.5)', borderRadius: '16px', padding: '4px' }}
            >
              <IonSegmentButton value="overview" className="rounded-xl">
                <IonLabel className="font-bold">OVERVIEW</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="bar" className="rounded-xl">
                <IonLabel className="font-bold">BAR CHART</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="pie" className="rounded-xl">
                <IonLabel className="font-bold">PIE CHART</IonLabel>
              </IonSegmentButton>
            </IonSegment>
          </div>

          {/* Poll Header Card - Modern Architecture */}
          <div className="glass-card-beautified p-10 text-left relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4">
              <div className="px-4 py-1.5 rounded-2xl text-xs font-black tracking-widest bg-emerald-100 text-emerald-600">
                ACTIVE INTELLIGENCE
              </div>
            </div>

            <div className="insight-icon-container mb-8 shadow-lg scale-110">
              <IonIcon icon={barChartOutline} className="text-2xl text-white" />
            </div>

            <h1 className="text-xl md:text-2xl font-bold text-slate-800 mb-6 leading-tight max-w-3xl poll-question-responsive">
              {poll.question}
            </h1>

            <div className="flex flex-wrap justify-start gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-2xl border border-slate-100">
                <IonIcon icon={calendarOutline} color="primary" />
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  DEPLOYED: {new Date(poll.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          <div id="analytics-export-container" className={isExporting ? "bg-white p-4" : ""}>
            {/* If exporting, we might want a header right inside the PDF */}
            {isExporting && (
              <div className="mb-6 border-b pb-4">
                <h2 className="text-2xl font-black text-slate-800">Poll Intelligence Report</h2>
                <p className="text-slate-500">{poll.question}</p>
                <p className="text-sm text-slate-400 mt-2">Generated on: {new Date().toLocaleString()}</p>
              </div>
            )}

            {/* Dynamic Content Views */}
            {(activeSegment === 'overview' || isExporting) && (
              <div className={isExporting ? "mb-10 page-break-avoid" : ""}>
                {/* Stats Grid - Premium Architecture */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {statCards.map((stat, index) => (
                    <div key={index} className="analytics-card-glass stat-card-premium border-none">
                      <div className="flex items-center justify-between mb-6">
                        <div className="rounded-2xl p-4 shadow-sm" style={{ background: stat.iconBg }}>
                          {isExporting ? (
                            <div
                              style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              dangerouslySetInnerHTML={{ __html: getExportSafeSvg(stat.icon, stat.iconColor) }}
                            />
                          ) : (
                            <IonIcon icon={stat.icon} className="text-3xl" style={{ color: stat.iconColor }} />
                          )}
                        </div>


                        <div className="text-xs font-bold px-3 py-1 rounded-full bg-emerald-100 text-emerald-600">
                          +{Math.floor(Math.random() * 20)}%
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-slate-400 tracking-[0.2em] mb-1 uppercase">{stat.label}</p>
                        <h3 className="text-4xl font-black text-slate-800 tracking-tighter">{stat.value.toLocaleString()}</h3>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Detailed Intelligence Distribution */}
                <div className="analytics-card-glass p-10 border-none">
                  <div className="flex items-center gap-4 mb-10">
                    <div className="insight-icon-container">
                      {isExporting ? (
                        <div
                          style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          dangerouslySetInnerHTML={{ __html: getExportSafeSvg(barChartOutline, '#ffffff') }}
                        />
                      ) : (
                        <IonIcon icon={barChartOutline} className="text-2xl text-white" />
                      )}
                    </div>


                    <div>
                      <h2 className="text-2xl font-black text-slate-800 tracking-tight">Intelligence Distribution</h2>
                      <p className="text-xs font-bold text-slate-400 tracking-widest uppercase">Granular Response Mapping</p>
                    </div>
                  </div>

                  {analytics.answers.length > 0 ? (
                    <div className="space-y-6">
                      {analytics.answers.map((answer, index) => {
                        const colorScheme = answerColors[index % answerColors.length];
                        return (
                          <div key={index} className="bg-slate-50/50 rounded-3xl p-6 border border-white transition-all duration-300 hover:bg-white hover:shadow-xl">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                              <div className="flex-1">
                                <h4 className="text-lg font-black text-slate-800 mb-1 tracking-tight">
                                  {answer.answer}
                                </h4>
                                <div className="flex items-center gap-2">
                                  <div className="w-2 h-2 rounded-full" style={{ background: colorScheme.gradient }}></div>
                                  <p className="text-[10px] font-black text-slate-400 tracking-widest uppercase">
                                    {answer.votes} COLLECTED DATA NODES
                                  </p>
                                </div>
                              </div>
                              <div className="text-3xl font-black text-slate-800 tracking-tighter">
                                {answer.percentage}%
                              </div>
                            </div>

                            {/* Premium Slim Progress Bar */}
                            <div className="w-full h-2.5 bg-white rounded-full overflow-hidden shadow-inner">
                              <div
                                style={{
                                  height: '100%',
                                  background: colorScheme.gradient,
                                  width: `${answer.percentage}%`,
                                  boxShadow: '0 0 15px rgba(34, 197, 94, 0.4)'
                                }}
                                className="rounded-full transition-all duration-1000 ease-out"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-20 bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                      <div className="text-6xl mb-4">📊</div>
                      <h3 className="text-xl font-black text-slate-800 mb-2 uppercase tracking-tight">No Data Collected</h3>
                      <p className="text-sm font-bold text-slate-400 tracking-widest">AWAITING SYSTEM PARTICIPATION</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(activeSegment === 'bar' || isExporting) && (
              <div className={`analytics-card-glass p-10 border-none min-h-[500px] ${isExporting ? 'mb-10 page-break-avoid' : ''}`}>
                <div className="flex items-center gap-4 mb-10">
                  <div className="insight-icon-container">
                    {isExporting ? (
                      <div
                        style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        dangerouslySetInnerHTML={{ __html: getExportSafeSvg(barChartOutline, '#ffffff') }}
                      />
                    ) : (
                      <IonIcon icon={barChartOutline} className="text-2xl text-white" />
                    )}

                  </div>

                  <div>
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight">Quantitative Analysis</h2>
                    <p className="text-xs font-bold text-slate-400 tracking-widest uppercase">Response Volume Comparison</p>
                  </div>
                </div>

                <div style={{ width: '100%', height: 400 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={analytics.answers.map(ans => ({ name: ans.answer, votes: ans.votes }))}
                      margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                        height={80}
                        tick={{ fill: '#64748b', fontSize: 12, fontWeight: 'bold' }}
                      />
                      <YAxis tick={{ fill: '#64748b', fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                        cursor={{ fill: '#f8fafc' }}
                      />
                      <Bar dataKey="votes" radius={[10, 10, 0, 0]} isAnimationActive={!isExporting}>
                        {analytics.answers.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#0d9488', '#059669', '#0891b2', '#7c3aed', '#db2777', '#ea580c'][index % 6]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {(activeSegment === 'pie' || isExporting) && (
              <div className={`analytics-card-glass p-10 border-none min-h-[500px] ${isExporting ? 'mb-10 page-break-avoid' : ''}`}>
                <div className="flex items-center gap-4 mb-10">
                  <div className="insight-icon-container">
                    {isExporting ? (
                      <div
                        style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        dangerouslySetInnerHTML={{ __html: getExportSafeSvg(barChartOutline, '#ffffff') }}
                      />
                    ) : (
                      <IonIcon icon={barChartOutline} className="text-2xl text-white" />
                    )}

                  </div>

                  <div>
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight">Proportional Intelligence</h2>
                    <p className="text-xs font-bold text-slate-400 tracking-widest uppercase">Response Ratio Distribution</p>
                  </div>
                </div>

                <div style={{ width: '100%', height: 400 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={analytics.answers.map(ans => ({ name: ans.answer, value: ans.votes }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={80}
                        outerRadius={140}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => percent > 0 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                        minAngle={15}
                        isAnimationActive={!isExporting}
                      >
                        {analytics.answers.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#0d9488', '#059669', '#0891b2', '#7c3aed', '#db2777', '#ea580c'][index % 6]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}
                      />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Engagement Insights - Premium Banner */}
            {analytics.totalVotes > 0 && (
              <div className="insight-banner-premium">
                <div className="flex items-center gap-4 mb-8">
                  <div className="insight-icon-container">
                    {isExporting ? (
                      <div
                        style={{ width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        dangerouslySetInnerHTML={{ __html: getExportSafeSvg(trendingUpOutline, '#ffffff') }}
                      />
                    ) : (
                      <IonIcon icon={trendingUpOutline} className="text-2xl text-white" />
                    )}
                  </div>


                  <div>
                    <h3 className="text-2xl font-black text-slate-800 tracking-tight">System Performance Metrics</h3>
                    <p className="text-xs font-bold text-slate-500 tracking-widest uppercase">Real-time Engagement Architecture</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {[
                    {
                      label: 'PARTICIPATION RATE',
                      value: `${analytics.viewCount > 0 ? ((analytics.uniqueVoters / analytics.viewCount) * 100).toFixed(0) : 0}%`,
                      desc: 'Conversion Frequency'
                    },
                    {
                      label: 'DOMINANT NODE',
                      value: (analytics.answers.reduce((max, ans) => parseInt(ans.votes) > parseInt(max.votes) ? ans : max, analytics.answers[0])?.answer || 'N/A').toUpperCase(),
                      desc: 'Primary Trend Index'
                    },
                    {
                      label: 'NODAL DENSITY',
                      value: (analytics.answers.length > 0 ? (analytics.totalVotes / analytics.answers.length).toFixed(1) : 0),
                      desc: 'Mean Option Volume'
                    }
                  ].map((insight, idx) => (
                    <div key={idx} className="bg-white/40 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-sm">
                      <p className="text-[10px] font-black text-slate-400 tracking-widest mb-1">{insight.label}</p>
                      <p className="text-xl font-black text-slate-800 mb-1 truncate" title={insight.value}>{insight.value}</p>
                      <p className="text-[10px] font-bold text-emerald-600 tracking-tighter uppercase">{insight.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div> {/* End export wrapper */}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default PollAnalytics;
